import { useEffect, useState } from "react";
import { formatDate, isoDay } from "../format.js";
import {
  declareInvoicePaid,
  getFamilyInvoices,
  type FamilyInvoice,
} from "../schoolInvoices.js";
import { useAsync } from "../useAsync.js";

/**
 * What a school family owes, and how to pay it.
 *
 * Parents pay school lessons by bank transfer, so the useful things here are
 * the amount, when it is due, the reference to quote, and every lesson date it
 * covers. There is no card processing behind any of it.
 *
 * "I have paid this" is a claim and nothing more: it tells the school to check
 * the bank. It does not settle the bill, and the tick appears only once the
 * school has confirmed the money arrived — so a family can say what they have
 * done without being able to clear their own debt.
 */

function periodLabel(invoice: FamilyInvoice): string {
  return `${formatDate(invoice.periodStart)} – ${formatDate(invoice.periodEnd)}`;
}

function today(): string {
  return isoDay();
}

/** Whether any credit came off this bill. */
function credited(invoice: FamilyInvoice): boolean {
  return invoice.creditApplied !== "0.00" && invoice.creditApplied !== "0";
}

/**
 * What the family actually owes.
 *
 * The invoice's own total is the gross — what the lessons came to — so every
 * figure shown to a family is this one, net of credit. Computed as pence to
 * avoid the floating-point drift that puts a bill a penny out.
 */
function toPay(invoice: FamilyInvoice): string {
  const pence = (value: string) => Math.round(Number(value) * 100);
  const owed = Math.max(0, pence(invoice.totalAmount) - pence(invoice.creditApplied || "0"));
  return (owed / 100).toFixed(2);
}

function InvoiceCard({
  invoice,
  payTo,
  onDeclared,
  historical = false,
}: {
  invoice: FamilyInvoice;
  payTo: string | null;
  onDeclared: () => void;
  historical?: boolean;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const due = invoice.status === "issued";
  const verified = invoice.status === "paid";
  const closed = invoice.status === "void";
  const declared = due && invoice.parentDeclaredPaidAt !== null;
  const late = due && invoice.dueOn !== null && invoice.dueOn < today();

  return (
    <article className="panel" style={{ marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <strong>£{toPay(invoice)}</strong>{" "}
          {verified && (
            <span className="tag" aria-label="Payment confirmed by the school">
              ✓ Paid
            </span>
          )}
          {closed && <span className="tag">Closed</span>}
          {declared && <span className="tag">Waiting to be checked</span>}
          {due && !declared && <span className="tag warn">Due</span>}
          <div className="small muted">School lessons · {periodLabel(invoice)}</div>
          {credited(invoice) && (
            /* The arithmetic, not just the answer. A family owed for a
               cancelled lesson should see that it came off — an invoice that
               simply arrived smaller than expected invites the query this is
               meant to prevent. */
            <div className="small muted">
              £{invoice.totalAmount} for lessons, less £{invoice.creditApplied} credited for
              lessons not taught
            </div>
          )}
          {invoice.dueOn && due && (
            <div className={late ? "small warn" : "small muted"}>
              Payment due by {formatDate(invoice.dueOn)}
            </div>
          )}
        </div>
        <div className="small muted" style={{ textAlign: "right" }}>
          <div>
            Reference <strong>{invoice.reference}</strong>
          </div>
          {invoice.paidAt && <div>Confirmed {formatDate(invoice.paidAt)}</div>}
        </div>
      </div>

      {due && (
        <div className="small" style={{ marginTop: 10 }}>
          {payTo ? (
            <>
              <div>Please pay by bank transfer to:</div>
              {/* The school's own wording, shown as written rather than parsed
                  into fields — it is read by a person and typed into a banking
                  app, and every school words it differently. */}
              <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{payTo}</div>
            </>
          ) : (
            <div>Please pay by bank transfer using the details the school gave you.</div>
          )}
          <div style={{ marginTop: 6 }}>
            Quote <strong>{invoice.reference}</strong> as the payment reference so we can match it.
          </div>
        </div>
      )}

      {invoice.notes && (
        <p className="small muted" style={{ marginTop: 8 }}>
          {invoice.notes}
        </p>
      )}

      {declared && (
        <p className="small muted" style={{ marginTop: 8 }}>
          You told us you paid this on {formatDate(invoice.parentDeclaredPaidAt ?? "")}. The school
          will confirm it once they have checked the bank.
        </p>
      )}

      {due && !declared && (
        <div className="stack" style={{ gap: 6, marginTop: 10 }}>
          <label className="small">
            <span>Anything to tell the school about the payment? (optional)</span>
            <input
              value={note}
              placeholder="Sent 14 September from J Hughes"
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
          <div>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                setProblem(null);
                void declareInvoicePaid(invoice.id, note.trim() || undefined)
                  .then(onDeclared)
                  .catch((cause: unknown) =>
                    setProblem(
                      cause instanceof Error ? cause.message : "That did not work.",
                    ),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              I have paid this
            </button>
          </div>
          {problem && <div className="error small">{problem}</div>}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <h4 className="small" style={{ marginBottom: 4 }}>
          {invoice.lines.length} lesson{invoice.lines.length === 1 ? "" : "s"} on this invoice
        </h4>
        {historical ? (
          <details>
            <summary className="small">Show lesson details</summary>
            <div className="stack" style={{ gap: 4, marginTop: 6 }}>
              {invoice.lines.map((line, index) => (
                <div
                  key={line.id ?? `${line.lessonDate}:${index}`}
                  className="small"
                  style={{ display: "flex", justifyContent: "space-between", gap: 12 }}
                >
                  <span>
                    {formatDate(line.lessonDate)} · {line.studentName} · {line.description}
                  </span>
                  <span>£{line.amount}</span>
                </div>
              ))}
            </div>
          </details>
        ) : (
          <div className="stack" style={{ gap: 4 }}>
            {invoice.lines.map((line, index) => (
              <div
                key={line.id ?? `${line.lessonDate}:${index}`}
                className="small"
                style={{ display: "flex", justifyContent: "space-between", gap: 12 }}
              >
                <span>
                  {formatDate(line.lessonDate)} · {line.studentName} · {line.description}
                </span>
                <span>£{line.amount}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

export function FamilyInvoices() {
  const [refresh, setRefresh] = useState(0);
  const { data, error, loading } = useAsync(
    () => getFamilyInvoices({ historyPage: 1, historyLimit: 10 }),
    [refresh],
  );
  const [olderSettled, setOlderSettled] = useState<FamilyInvoice[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const reload = () => setRefresh((value) => value + 1);

  useEffect(() => {
    if (!data) return;
    setOlderSettled([]);
    setHistoryPage(data.historyPage ?? 1);
    setHistoryHasMore(data.historyHasMore === true);
    setHistoryError(null);
  }, [data]);

  if (loading) return <p className="empty">Loading…</p>;
  if (error) return <div className="error">{error}</div>;
  if (!data) return null;

  const due = data.invoices.filter((invoice) => invoice.status === "issued");
  const initialSettled = data.invoices.filter(
    (invoice) => invoice.status === "paid" || invoice.status === "void",
  );
  const settled = [...initialSettled, ...olderSettled].filter(
    (invoice, index, all) => all.findIndex((item) => item.id === invoice.id) === index,
  );
  const historyLimit = data.historyLimit ?? 10;

  async function loadOlderInvoices() {
    if (historyLoading || !historyHasMore) return;
    const nextPage = historyPage + 1;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const next = await getFamilyInvoices({
        historyPage: nextPage,
        historyLimit,
      });
      const nextSettled = next.invoices.filter(
        (invoice) => invoice.status === "paid" || invoice.status === "void",
      );
      setOlderSettled((current) => {
        const known = new Set([...initialSettled, ...current].map((invoice) => invoice.id));
        return [...current, ...nextSettled.filter((invoice) => !known.has(invoice.id))];
      });
      setHistoryPage(nextPage);
      setHistoryHasMore(next.historyHasMore === true);
    } catch (cause: unknown) {
      setHistoryError(
        cause instanceof Error ? cause.message : "Could not load older invoices.",
      );
    } finally {
      setHistoryLoading(false);
    }
  }

  return (
    <>
      <h2>Invoices</h2>

      {due.length === 0 && settled.length === 0 && (
        <p className="empty">
          Nothing to pay at the moment. School lesson invoices appear here when the school sends
          one.
        </p>
      )}

      {due.length > 0 && (
        <section aria-labelledby="family-invoices-due">
          <h3 id="family-invoices-due">Due</h3>
          {due.map((invoice) => (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              payTo={data.payTo}
              onDeclared={reload}
            />
          ))}
        </section>
      )}

      {settled.length > 0 && (
        <section aria-labelledby="family-invoices-history">
          <h3 id="family-invoices-history">Previous invoices</h3>
          {settled.map((invoice) => (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              payTo={data.payTo}
              onDeclared={reload}
              historical
            />
          ))}
          {historyHasMore && (
            <div className="row" style={{ alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                className="action-tertiary small"
                disabled={historyLoading}
                onClick={() => void loadOlderInvoices()}
              >
                {historyLoading ? "Loading older…" : "Show older invoices"}
              </button>
              {historyError && <span className="error small">{historyError}</span>}
            </div>
          )}
        </section>
      )}
    </>
  );
}
